import { AssessmentSliceActions } from "@assessment/store/slices/assessmentSlice";
import { useAppDispatch, useAppSelector } from "@assessment/store/store";
import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions
} from "@headlessui/react";
import { cn } from "@lxp-app/utils/cn";
import GetHeroIcon from "@lxp-app/utils/getHeroIcon";
import Tooltip from "@mui/material/Tooltip";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

interface LanguageProps {
  questionData: any;
  editorLanguage: any;
  setEditorLanguage: (arg: any) => void;
  handleSave: () => void;
  editorTheme: "vs-dark" | "light";
  showLoader: boolean;
  runTestCases: () => void;
  submitAnswers: () => void;
  setEditorTheme: (arg: "vs-dark" | "light") => void;
  isCodeChange: boolean;
  testCaseType: "sample" | "hidden";
}

const LanguageDropdown = ({
  questionData,
  editorLanguage,
  editorTheme,
  setEditorLanguage,
  showLoader,
  runTestCases,
  submitAnswers,
  setEditorTheme,
  handleSave,
  isCodeChange,
  testCaseType
}: LanguageProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [languageArr, setLanguageArr] = useState([]);

  const editorFullScreen = useAppSelector(
    (store) => store.AssessmentSlice.editorFullScreen
  );

  const activeQuestionMeta = useAppSelector(
    (store) => store.AssessmentSlice.activeQuestionMeta
  );

  useEffect(() => {
    dispatch(AssessmentSliceActions.mutateFullScreen(false));
  }, [activeQuestionMeta?.questionId]);

  useEffect(() => {
    formatLanguage(questionData?.allowedCodingLanguages);
  }, [questionData]);

  const formatLanguage = (langArr: any) => {
    const formattedArr = langArr.map((el: any, index: number) => {
      return { id: index, value: el, name: el };
    });
    setEditorLanguage(formattedArr[0]);
    setLanguageArr(formattedArr);
  };

  const languageChange = (e: any) => {
    if (isCodeChange) {
      handleSave();
      const temp = languageArr.find((el: any) => el?.name == e);
      setEditorLanguage(temp);
    } else {
      const temp = languageArr.find((el: any) => el?.name == e);
      setEditorLanguage(temp);
    }
  };

  const themeSwitchHandler = () => {
    if (editorTheme === "vs-dark") setEditorTheme("light");
    else setEditorTheme("vs-dark");
  };

  return (
    <>
      <div className="mb-10 flex w-full items-center justify-between gap-3 px-3 py-1 sm:px-5">
        <div className="w-[144px] not-dark sm:w-[200px]">
          {editorLanguage && languageArr && (
            <Listbox
              value={editorLanguage}
              onChange={(e: any) => languageChange(e)}
            >
              <div className="relative w-full">
                <ListboxButton
                  className={cn(
                    "flex w-full items-center justify-between rounded-md border px-2.5 py-1.5 text-sm sm:px-3 sm:py-2 sm:text-base",
                    editorTheme === "vs-dark" &&
                      "bg-[#1E1E1E]  border-gray-500 text-white",
                    editorTheme === "light" &&
                      "border-gray-200 dark:border-slate-400 bg-white dark:text-black"
                  )}
                >
                  <span className="flex-1 text-left truncate">
                    {editorLanguage?.name}
                  </span>
                  <GetHeroIcon
                    name="ChevronUpDownIcon"
                    variant="outline"
                    className="h-4 w-4 flex-shrink-0 sm:h-5 sm:w-5"
                  />
                </ListboxButton>
                <ListboxOptions
                  className={cn(
                    "absolute z-20 w-full max-h-60 rounded-b-xl shadow overflow-y-auto thin-scrollbar",
                    editorTheme === "vs-dark" &&
                      "bg-secondary text-white dark:bg-[#343434]",
                    editorTheme === "light" &&
                      "border-slate-400 bg-white dark:text-black"
                  )}
                >
                  {languageArr?.length > 0 &&
                    languageArr.map((el: any, index) => {
                      return (
                        <ListboxOption
                          key={el?.id}
                          value={el?.value}
                          className={cn(
                            "border-b last:border-none py-3 px-2 cursor-pointer",
                            editorTheme === "vs-dark" &&
                              "border-gray-500 hover:bg-neutral-800",
                            editorTheme === "light" && "hover:bg-neutral-100"
                          )}
                        >
                          {({}) => <div>{el?.name}</div>}
                        </ListboxOption>
                      );
                    })}
                </ListboxOptions>
              </div>
            </Listbox>
          )}
        </div>

        {/* run and submit buttons */}
        <div className="flex gap-x-px sm:-translate-x-14">
          <Tooltip
            title={
              showLoader && testCaseType == "sample"
                ? t("assessment.coding.runTests")
                : t("assessment.coding.run")
            }
          >
            <button
              className={cn(
                "rounded-l-lg px-3 py-1.5 pr-2 sm:px-4 sm:py-2 text-sm font-medium sm:text-base",
                editorTheme === "vs-dark" && "bg-[#343434] text-gray-100",
                editorTheme === "light" && "bg-[#f5f5f5] text-gray-400",
                showLoader && "cursor-not-allowed flex text-gray-500"
              )}
              onClick={() => runTestCases()}
            >
              {showLoader && testCaseType == "sample" ? (
                <div
                  className={cn(
                    "mt-1 w-4 h-4 border-2 border-gray-300 border-t-green-500 rounded-full animate-spin mr-2"
                  )}
                />
              ) : (
                <GetHeroIcon
                  name={"PlayIcon"}
                  className="size-4 text-gray-500 sm:size-5"
                />
              )}
              {showLoader &&
                testCaseType == "sample" &&
                t("assessment.coding.runTests")}
            </button>
          </Tooltip>

          <Tooltip
            title={
              showLoader && testCaseType == "hidden"
                ? t("assessment.coding.submitting")
                : t("assessment.coding.submit")
            }
          >
            <button
              type="button"
              className={cn(
                "rounded-r-lg px-3 py-1.5 pl-2 text-sm font-medium sm:px-4 sm:py-2 sm:text-base",
                editorTheme === "vs-dark" && "bg-[#343434] text-[#28c244]",
                editorTheme === "light" && "bg-[#f5f5f5] text-[#01b328]",
                showLoader && "cursor-not-allowed flex text-gray-500"
              )}
              onClick={() => submitAnswers()}
            >
              {showLoader && testCaseType == "hidden" && (
                <div
                  className={cn(
                    "mt-1 w-4 h-4 border-2 border-gray-300 border-t-green-500 rounded-full animate-spin mr-2"
                  )}
                />
              )}
              {showLoader && testCaseType == "hidden"
                ? t("assessment.coding.submitting")
                : t("assessment.coding.submit")}
            </button>
          </Tooltip>
        </div>

        {/* Options */}
        <div className="flex items-center gap-2 sm:gap-3">
          <button type="button" onClick={themeSwitchHandler}>
            {editorTheme === "vs-dark" ? (
              <GetHeroIcon
                name="SunIcon"
                className="h-6 w-6 text-slate-400 sm:h-8 sm:w-8"
              />
            ) : (
              <GetHeroIcon
                name="MoonIcon"
                className="h-6 w-6 text-[#1e1e1e] sm:h-8 sm:w-8"
              />
            )}
          </button>
          <button
            type="button"
            onClick={() => {
              if (editorFullScreen)
                dispatch(AssessmentSliceActions.mutateFullScreen(false));
              else dispatch(AssessmentSliceActions.mutateFullScreen(true));
            }}
          >
            <GetHeroIcon
              name={
                editorFullScreen
                  ? "ArrowsPointingInIcon"
                  : "ArrowsPointingOutIcon"
              }
              variant="outline"
              className={cn(
                "h-6 w-6 sm:h-7 sm:w-7",
                editorTheme === "vs-dark" && "text-slate-400",
                editorTheme === "light" && "text-[#1e1e1e]"
              )}
            />
          </button>
        </div>
      </div>
    </>
  );
};

export default LanguageDropdown;
